/**
 * Framework-free search-history core.
 *
 * Shared by the React `useSearchHistory` hook and the embeddable widget, which
 * has no React. Keep this module free of framework imports.
 */

export const HISTORY_STORAGE_KEY = 'enterprise-search:history';
export const HISTORY_MAX_ENTRIES = 100;

export function loadHistory(storageKey: string = HISTORY_STORAGE_KEY): string[] {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is string => typeof entry === 'string');
  } catch {
    return [];
  }
}

export function saveHistory(
  entries: string[],
  storageKey: string = HISTORY_STORAGE_KEY
): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(entries));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/** Add a query to the top of the history, deduplicating and capping. */
export function addHistoryEntry(
  entries: string[],
  query: string,
  maxEntries: number = HISTORY_MAX_ENTRIES
): string[] {
  const trimmed = query.trim();
  if (!trimmed) return entries;
  return [trimmed, ...entries.filter((q) => q !== trimmed)].slice(0, maxEntries);
}

export function removeHistoryEntry(entries: string[], query: string): string[] {
  return entries.filter((q) => q !== query);
}
